import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { OptionButton } from '@ui/components/OptionButton';
import { TriviaConfig, TRIVIA_QUESTIONS, type TriviaQuestion } from '@modules/trivia/config';

const OPTION_WIDTH = 720;
const OPTION_SPACING = 92;
const FIRST_OPTION_Y_RATIO = 0.42;

/**
 * Minijuego de trivia: muestra preguntas de opcion multiple, da feedback de
 * correcto/incorrecto y suma puntos. Tercer ejemplo del patron modular; reutiliza
 * el componente OptionButton para los botones de respuesta.
 */
export class TriviaScene extends BaseScene {
  private questions: TriviaQuestion[] = [];
  private currentIndex = 0;
  private score = 0;
  private optionButtons: OptionButton[] = [];
  private questionObjects: Phaser.GameObjects.GameObject[] = [];
  private scoreText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;

  public constructor() {
    super(TriviaConfig.key);
  }

  public create(): void {
    this.resetState();
    this.add.rectangle(this.centerX, this.centerY, this.scale.width, this.scale.height, Palette.backgroundAlt);

    this.scoreText = this.add.text(40, 30, '', {
      fontFamily: 'Trebuchet MS, sans-serif',
      fontSize: '34px',
      color: Palette.textLight,
    });

    this.progressText = this.add
      .text(this.scale.width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.renderQuestion();
  }

  private resetState(): void {
    this.questions = Phaser.Utils.Array.Shuffle([...TRIVIA_QUESTIONS]).slice(
      0,
      TriviaConfig.questionsPerRound,
    );
    this.currentIndex = 0;
    this.score = 0;
    this.optionButtons = [];
    this.questionObjects = [];
  }

  private renderQuestion(): void {
    this.clearQuestion();
    this.refreshHud();

    const question = this.questions[this.currentIndex];

    const questionText = this.add
      .text(this.centerX, this.scale.height * 0.24, question.question, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '36px',
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: this.scale.width * 0.8 },
      })
      .setOrigin(0.5);
    this.questionObjects.push(questionText);

    const firstY = this.scale.height * FIRST_OPTION_Y_RATIO;
    question.options.forEach((option, index) => {
      const button = new OptionButton(this, {
        x: this.centerX,
        y: firstY + index * OPTION_SPACING,
        width: OPTION_WIDTH,
        label: option,
        onSelect: () => this.handleAnswer(index),
      });
      this.optionButtons.push(button);
    });
  }

  private handleAnswer(selectedIndex: number): void {
    const question = this.questions[this.currentIndex];
    const isCorrect = selectedIndex === question.correctIndex;

    this.optionButtons.forEach((button, index) => {
      button.disable();
      if (index === question.correctIndex) {
        button.markCorrect();
      } else if (index === selectedIndex) {
        button.markWrong();
      }
    });

    if (isCorrect) {
      this.score += TriviaConfig.pointsPerCorrect;
      this.audio.playCorrect();
      this.refreshHud();
    } else {
      this.audio.playWrong();
    }

    this.time.delayedCall(TriviaConfig.feedbackDelayMs, () => this.advance());
  }

  private advance(): void {
    this.currentIndex += 1;
    if (this.currentIndex >= this.questions.length) {
      this.endRound();
      return;
    }
    this.renderQuestion();
  }

  private clearQuestion(): void {
    this.optionButtons.forEach((button) => button.destroy());
    this.questionObjects.forEach((object) => object.destroy());
    this.optionButtons = [];
    this.questionObjects = [];
  }

  private refreshHud(): void {
    this.scoreText.setText(`Puntos: ${this.score}`);
    this.progressText.setText(`Pregunta ${this.currentIndex + 1}/${this.questions.length}`);
  }

  private endRound(): void {
    this.clearQuestion();

    const isRecord = this.profile.submitScore(TriviaConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * TriviaConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.showResults(isRecord, coins);
  }

  private showResults(isRecord: boolean, coins: number): void {
    const overlay = this.add.rectangle(
      this.centerX,
      this.centerY,
      this.scale.width,
      this.scale.height,
      Palette.background,
      0.85,
    );
    overlay.setInteractive();

    this.add
      .text(this.centerX, this.centerY - 150, isRecord ? '¡Nuevo record!' : '¡Bien hecho!', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '52px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, this.centerY - 60, `Puntos: ${this.score}\nEstrellas ganadas: ${coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    new TextButton(this, {
      x: this.centerX - 180,
      y: this.centerY + 80,
      label: 'Jugar de nuevo',
      color: Palette.secondary,
      onClick: () => this.scene.restart(),
    });

    new TextButton(this, {
      x: this.centerX + 180,
      y: this.centerY + 80,
      label: 'Volver al menu',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }
}
